@let plan = data();

<mat-card appearance="outlined" class="ai-card" [class.plan-card--dimmed]="plan.state === 'rejected'">

  <!-- Header (redesign): clamped 2-line objective — the ONE sanctioned truncation, full text
       rides the matTooltip — lifecycle state icon (20px, word in the tooltip only, V2 ruling)
       and revision/dry-run badges right, vertically centered -->
  <div class="ai-card__header d-flex align-items-center gap-2 px-3 py-2">
    <div class="d-flex flex-column flex-fill min-w-0">
      <span class="plan-card__title mat-font-title-sm fw-medium" [matTooltip]="plan.request_objective">{{ plan.display_name || plan.request_objective }}</span>
      @if (plan.requested_change; as requestedChange) {
        <span class="mat-font-label-sm mat-text-on-surface-variant" [matTooltip]="requestedChange">Change requested: {{ requestedChange }}</span>
      }
    </div>
    <div class="d-flex align-items-center gap-2 flex-shrink-0">
      @if (headerState(); as stateIcon) {
        <mat-icon class="mat-icon-md" [class]="stateIcon.stateClass" [matTooltip]="stateIcon.word">{{ stateIcon.icon }}</mat-icon>
      }
      <span class="notification-badge mat-font-label-sm notification-badge--pending">rev. {{ plan.revision }}</span>
      @if (dryRun()) {
        <span class="notification-badge mat-font-label-sm notification-badge--pending">dry run</span>
      }
    </div>
  </div>

  <mat-card-content class="d-flex flex-column gap-2 px-3 pt-2 pb-3">

    <!-- Approval box (redesign): ONLY while the plan waits on approval — warn-token container
         with the unresolved decision checklist (readable titles, defensive payload readouts,
         Mandatory/Optional chips) and the first profile workarea. No approve controls: the
         decision happens through the conversation (doc 31 §10.1). -->
    @if (showApprovalBox()) {
      <div class="plan-card__warnbox d-flex flex-column gap-2">
        <div class="d-flex align-items-center gap-2">
          <mat-icon class="mat-icon-sm ai-card__warning-icon flex-shrink-0" aria-hidden="true">pending_actions</mat-icon>
          <span class="mat-font-label-sm fw-medium">Approval</span>
        </div>
        @for (row of pendingDecisionRows(); track row.decision.decision_id) {
          <!-- Decision row grid: 16px icon / title + reason + payload / Mandatory chip rail.
               Payload reads are DEFENSIVE (open dict): volume row when the payload carries a
               numeric volume, sample-size row when it carries sample_size, "Volume unknown"
               warning badge when volume_known === false. -->
          <div class="plan-card__decision-row">
            <mat-icon class="mat-icon-sm ai-card__warning-icon flex-shrink-0" aria-hidden="true">{{ row.icon }}</mat-icon>
            <div class="d-flex flex-column min-w-0">
              <span class="mat-font-body-md">{{ row.title }}</span>
              <span class="mat-font-label-sm mat-text-on-surface-variant">{{ row.decision.reason }}</span>
              @if (row.volume !== null || row.sampleSize !== null || row.volumeUnknown) {
                <div class="plan-card__payload align-items-center">
                  @if (row.volume !== null) {
                    <span class="mat-font-label-md">Volume: <b>{{ row.volume }}</b></span>
                  }
                  @if (row.sampleSize !== null) {
                    <span class="mat-font-label-md">Sample size: <b>{{ row.sampleSize }}</b></span>
                  }
                  @if (row.volumeUnknown) {
                    <span class="notification-badge mat-font-label-sm notification-badge--warning" matTooltip="The item volume could only be estimated">
                      <mat-icon class="mat-icon-xs" aria-hidden="true">help_outline</mat-icon>
                      Volume unknown
                    </span>
                  }
                </div>
              }
            </div>
            <span class="plan-card__chip mat-font-label-sm" [matTooltip]="row.decision.mandatory ? 'Mandatory decision' : 'Optional decision'">
              {{ row.decision.mandatory ? "Mandatory" : "Optional" }}
            </span>
          </div>
        }
        @if (approvalWorkarea(); as workarea) {
          <!-- R4d: the workarea reason is the row text; the raw id stays in the profile data
               (the used-by count gives the trace) -->
          <div class="d-flex flex-column">
            <span class="mat-font-label-sm mat-text-on-surface-variant">Workarea</span>
            <span class="mat-font-body-sm">{{ workarea.text }}</span>
            <span class="mat-font-label-sm mat-text-on-surface-variant">{{ workarea.usedByText }}</span>
          </div>
        }
      </div>
    }

    <!-- Steps (redesign): ALWAYS visible, no accordion — the shared grouped-step list renders
         each step with its confidence badge and per-task states; the plan card joins the LIVE
         engine rows by task_id (doc 31 §7.5), falling back to the persisted run_engine states -->
    <app-plan-step-list [steps]="stepListSteps()" [showConfidence]="true" />

    <!-- Execution outcome (redesign row), only once executed (doc 31 §10.1): all_produced in
         the ok tone; partial/none_produced keep the same row style in the err tone — raw undone
         ids ride the tooltip while intents render as text -->
    @if (outcomeRow(); as outcome) {
      <hr class="plan-card__divider">
      <div class="d-flex align-items-start gap-2" [matTooltip]="outcome.tooltip">
        <mat-icon class="mat-icon-md flex-shrink-0" [class]="outcome.stateClass" aria-hidden="true">{{ outcome.icon }}</mat-icon>
        <span class="mat-font-body-md">{{ outcome.text }}</span>
      </div>
    }

    <!-- Failure/diagnosis (redesign): blocked plans (failed / pending_feedback) — user message
         in the error tone, the technical diagnosis, and the active feedback questions as
         INFORMATIONAL rows (answers go through the conversation, never a form) -->
    @if (failureSection(); as failure) {
      <hr class="plan-card__divider">
      <div class="d-flex flex-column gap-2">
        <span class="mat-font-label-sm text-uppercase mat-text-on-surface-variant">Failure</span>
        @if (failure.message; as userMessage) {
          <div class="d-flex align-items-start gap-2">
            <mat-icon class="mat-icon-md mat-text-error flex-shrink-0" aria-hidden="true">error</mat-icon>
            <span class="mat-font-body-md mat-text-error">{{ userMessage }}</span>
          </div>
        }
        <div class="d-flex flex-column">
          <span class="mat-font-label-sm mat-text-on-surface-variant">Diagnosis</span>
          <span class="mat-font-body-sm">{{ failure.diagnosis }}</span>
        </div>
        @if (failure.questions.length > 0) {
          <div class="d-flex flex-column gap-1">
            <span class="mat-font-label-sm mat-text-on-surface-variant">Feedback options</span>
            @for (question of failure.questions; track question.question_id) {
              <!-- Full question text on the surface; the answer options ride the tooltip -->
              <div class="plan-card__option-row" [matTooltip]="question.options.length > 0 ? question.options.join(' · ') : ''">
                <mat-icon class="mat-icon-sm mat-text-primary flex-shrink-0" aria-hidden="true">help_outline</mat-icon>
                <span class="mat-font-body-sm">{{ question.text }}</span>
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- Context (redesign): DEBUG-ONLY label-above-value block for the profile pieces —
         batch (entity type + source ref), target entity, first workarea id; missing pieces
         fall back to "—" so the block renders even before the assessment populated the profile.
         Gated behind DebugModeService (2026-10-20 ruling): raw profile ids are internals. -->
    @if (isDebugMode()) {
      <div class="plan-card__context mat-bg-surface-container-low mat-corner-md">
        <span class="mat-font-label-sm text-uppercase mat-text-on-surface-variant">Context</span>
        @for (row of contextRows(); track row.label) {
          <div class="d-flex flex-column">
            <span class="mat-font-label-sm mat-text-on-surface-variant">{{ row.label }}</span>
            <span class="mat-font-body-md">{{ row.value }}</span>
          </div>
        }
      </div>
    }

    <!-- Footer (redesign): Debug opens the full debug dialog (visible only in debug mode via
         the DebugModeService signal — no input chaining); the raw plan dump lives only in that
         dialog. The button reads 32px via the M3 container-height tokens scoped in the scss. -->
    @if (isDebugMode()) {
      <div class="plan-card__footer d-flex align-items-center">
        <button matButton="outlined" type="button" (click)="openDebug()">
          <mat-icon class="mat-icon-sm" aria-hidden="true">bug_report</mat-icon>
          Debug
        </button>
      </div>
    }

  </mat-card-content>

</mat-card>